import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { apiFetch, put } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { keys } from "@/hooks/keys";
import { API_BASE } from "@/lib/utils";

export type HubConfig = Record<string, unknown>;

/** The row as the API returns it: the config plus the version a publish must
 *  quote back so a stale tab cannot overwrite someone else's publish. */
export interface HubSettingsRow {
  config: HubConfig;
  updatedAt: string | null;
}

function hubSettingsQuery(orgId: string | undefined) {
  return {
    queryKey: keys.hubSettings.get(orgId),
    queryFn: () => apiFetch<HubSettingsRow>(`${API_BASE}/v1/hub-settings`),
    enabled: !!orgId,
    staleTime: 60_000,
  };
}

export function useHubSettings() {
  const { userContext } = useAuth();
  // One query, two views. `select` runs per-subscriber, so the config consumers
  // are untouched while the builder can also read the version beside it.
  return useQuery({ ...hubSettingsQuery(userContext?.orgId), select: (r: HubSettingsRow) => r.config });
}

/** The version the loaded config came from, for the publish's concurrency check. */
export function useHubSettingsVersion() {
  const { userContext } = useAuth();
  return useQuery({ ...hubSettingsQuery(userContext?.orgId), select: (r: HubSettingsRow) => r.updatedAt });
}

/** Thrown when the publish was refused because the row moved underneath it. */
export class HubSettingsConflictError extends Error {
  constructor(message: string, readonly current: HubSettingsRow) {
    super(message);
    this.name = "HubSettingsConflictError";
  }
}

export function useSaveHubSettings() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: async ({ config, baseUpdatedAt }: { config: HubConfig; baseUpdatedAt?: string | null }) => {
      try {
        return await apiFetch<HubSettingsRow>(
          `${API_BASE}/v1/hub-settings`,
          put({ config, ...(baseUpdatedAt ? { baseUpdatedAt } : {}) }),
        );
      } catch (err) {
        // The 409 carries the config it refused to let us overwrite. Surfacing
        // it as its own error type is what lets the builder say "someone else
        // published" rather than showing a generic save failure for a write that
        // was deliberately declined.
        const body = (err as { body?: { code?: string; data?: HubSettingsRow } }).body;
        if (body?.code === "HUB_SETTINGS_STALE" && body.data) {
          queryClient.setQueryData(keys.hubSettings.get(orgId), body.data);
          throw new HubSettingsConflictError((err as Error).message, body.data);
        }
        throw err;
      }
    },
    onSuccess: (row) => {
      queryClient.setQueryData(keys.hubSettings.get(orgId), row);
    },
  });
}
